import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/lib/api';
import {
  PlusIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface StripePrice {
  id: string;
  active: boolean;
  currency: string;
  unitAmount: number | null;
  recurring: {
    interval: string;
    intervalCount: number;
  } | null;
  nickname: string | null;
  metadata: Record<string, string>;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  metadata: Record<string, string>;
  prices: StripePrice[];
  created: number;
}

interface PlanSync {
  tier: string;
  name: string;
  synced: boolean;
  stripe: {
    productId?: string;
    productName?: string;
    stripePriceIdMonthly?: string;
    stripePriceIdYearly?: string;
    monthlyPrice?: number;
    yearlyPrice?: number;
  } | null;
}

export default function AdminBilling() {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [plans, setPlans] = useState<PlanSync[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreatePrice, setShowCreatePrice] = useState(false);
  const [_selectedProduct, _setSelectedProduct] = useState<StripeProduct | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'sync'>('products');

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    tier: '',
  });

  const [newPrice, setNewPrice] = useState({
    productId: '',
    unitAmount: 0,
    interval: 'month' as 'month' | 'year',
    nickname: '',
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await adminApi.listStripeProducts({ active: true });
      if (response.data.success) {
        setProducts(response.data.data.products);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load Stripe products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncPlans = async () => {
    setIsSyncing(true);
    try {
      const response = await adminApi.syncPricingPlans();
      if (response.data.success) {
        setPlans(response.data.data.plans);
        toast.success(`Synced ${response.data.data.syncedCount}/${response.data.data.totalPlans} plans`);
        loadProducts(); // Refresh products list
      }
    } catch (error) {
      console.error('Failed to sync plans:', error);
      toast.error('Failed to sync pricing plans');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    setIsCreating(true);
    try {
      const response = await adminApi.createStripeProduct({
        name: newProduct.name.trim(),
        description: newProduct.description.trim() || undefined,
        metadata: newProduct.tier ? { tier: newProduct.tier } : undefined,
      });

      if (response.data.success) {
        toast.success('Product created successfully');
        setShowCreateProduct(false);
        setNewProduct({ name: '', description: '', tier: '' });
        loadProducts();
      }
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error('Failed to create product');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreatePrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPrice.productId) {
      toast.error('Please select a product');
      return;
    }
    if (newPrice.unitAmount < 0) {
      toast.error('Price must be positive');
      return;
    }

    setIsCreating(true);
    try {
      const response = await adminApi.createStripePrice({
        productId: newPrice.productId,
        unitAmount: Math.round(newPrice.unitAmount * 100), // Convert to cents
        currency: 'usd',
        nickname: newPrice.nickname || undefined,
        recurring: {
          interval: newPrice.interval,
          intervalCount: 1,
        },
      });

      if (response.data.success) {
        toast.success('Price created successfully');
        setShowCreatePrice(false);
        setNewPrice({ productId: '', unitAmount: 0, interval: 'month', nickname: '' });
        loadProducts();
      }
    } catch (error) {
      console.error('Failed to create price:', error);
      toast.error('Failed to create price');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleProductActive = async (product: StripeProduct) => {
    try {
      await adminApi.updateStripeProduct(product.id, { active: !product.active });
      toast.success(`Product ${product.active ? 'deactivated' : 'activated'}`);
      loadProducts();
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error('Failed to update product');
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatInterval = (recurring: StripePrice['recurring']) => {
    if (!recurring) return 'One-time';
    if (recurring.intervalCount === 1) {
      return recurring.interval === 'month' ? 'Monthly' : 'Yearly';
    }
    return `Every ${recurring.intervalCount} ${recurring.interval}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-display text-2xl font-bold text-charcoal">Stripe Billing</h2>
          <p className="text-charcoal/70 mt-1">Manage products, prices, and pricing plans</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSyncPlans}
            disabled={isSyncing}
            className="btn btn-secondary flex items-center gap-2"
          >
            <ArrowPathIcon className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} />
            Sync Plans
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-cream">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('products')}
            className={`py-3 border-b-2 font-medium text-sm ${
              activeTab === 'products'
                ? 'border-rust text-rust'
                : 'border-transparent text-charcoal/60 hover:text-charcoal'
            }`}
          >
            Products & Prices
          </button>
          <button
            onClick={() => { setActiveTab('sync'); handleSyncPlans(); }}
            className={`py-3 border-b-2 font-medium text-sm ${
              activeTab === 'sync'
                ? 'border-rust text-rust'
                : 'border-transparent text-charcoal/60 hover:text-charcoal'
            }`}
          >
            Plan Sync Status
          </button>
        </nav>
      </div>

      {activeTab === 'products' && (
        <>
          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateProduct(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Create Product
            </button>
            <button
              onClick={() => setShowCreatePrice(true)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <CurrencyDollarIcon className="w-5 h-5" />
              Add Price
            </button>
          </div>

          {/* Products List */}
          <div className="space-y-4">
            {products.length === 0 ? (
              <div className="rounded-xl bg-warm-white shadow-sm border border-cream p-8 text-center">
                <TagIcon className="w-12 h-12 mx-auto text-charcoal/40 mb-4" />
                <h3 className="font-display text-lg font-medium text-charcoal mb-2">No Products Found</h3>
                <p className="text-charcoal/60 mb-4">
                  Create your first Stripe product to get started with billing.
                </p>
                <button
                  onClick={() => setShowCreateProduct(true)}
                  className="btn btn-primary"
                >
                  Create Product
                </button>
              </div>
            ) : (
              products.map((product) => (
                <div key={product.id} className="rounded-xl bg-warm-white shadow-sm border border-cream">
                  <div className="px-6 py-4 border-b border-cream flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-lg font-semibold text-charcoal">{product.name}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          product.active
                            ? 'bg-sage/20 text-sage'
                            : 'bg-cream text-charcoal/60'
                        }`}>
                          {product.active ? 'Active' : 'Inactive'}
                        </span>
                        {product.metadata?.tier && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-rust/20 text-rust">
                            {product.metadata.tier}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-charcoal/60 mt-1">
                        {product.description || 'No description'}
                      </p>
                      <p className="text-xs text-charcoal/40 mt-1">ID: {product.id}</p>
                    </div>
                    <button
                      onClick={() => handleToggleProductActive(product)}
                      className={`btn btn-sm ${product.active ? 'btn-danger' : 'btn-primary'}`}
                    >
                      {product.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                  <div className="p-6">
                    <h4 className="text-sm font-medium text-charcoal/70 mb-3">Prices</h4>
                    {product.prices.length === 0 ? (
                      <p className="text-charcoal/50 text-sm">No prices configured</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {product.prices.map((price) => (
                          <div
                            key={price.id}
                            className={`p-3 rounded-lg border ${
                              price.active
                                ? 'border-cream bg-cream/30'
                                : 'border-cream/50 bg-cream/10 opacity-60'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-serif text-lg font-semibold text-charcoal">
                                  {formatCurrency(price.unitAmount)}
                                </p>
                                <p className="text-sm text-charcoal/60">
                                  {formatInterval(price.recurring)}
                                </p>
                                {price.nickname && (
                                  <p className="text-xs text-rust mt-1">{price.nickname}</p>
                                )}
                              </div>
                              {price.active ? (
                                <CheckCircleIcon className="w-5 h-5 text-sage" />
                              ) : (
                                <XCircleIcon className="w-5 h-5 text-charcoal/40" />
                              )}
                            </div>
                            <p className="text-xs text-charcoal/40 mt-2 truncate">{price.id}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {activeTab === 'sync' && (
        <div className="space-y-4">
          {plans.length === 0 ? (
            <div className="rounded-xl bg-warm-white shadow-sm border border-cream p-8 text-center">
              <ArrowPathIcon className="w-12 h-12 mx-auto text-charcoal/40 mb-4" />
              <h3 className="font-display text-lg font-medium text-charcoal mb-2">Click "Sync Plans" to check status</h3>
              <p className="text-charcoal/60">
                This will match your Stripe products to the platform pricing tiers.
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-warm-white shadow-sm border border-cream">
              <div className="px-6 py-4 border-b border-cream">
                <h3 className="font-display text-lg font-semibold text-charcoal">Pricing Plan Sync Status</h3>
                <p className="text-sm text-charcoal/60 mt-1">
                  Match Stripe products to platform tiers using the product's tier metadata
                </p>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {plans.map((plan) => (
                    <div
                      key={plan.tier}
                      className={`p-4 rounded-lg border ${
                        plan.synced
                          ? 'border-sage/30 bg-sage/5'
                          : 'border-cream bg-cream/30'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium text-charcoal">{plan.name}</h4>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              plan.synced
                                ? 'bg-sage/20 text-sage'
                                : 'bg-tan/20 text-tan'
                            }`}>
                              {plan.synced ? 'Synced' : 'Not Synced'}
                            </span>
                          </div>
                          <p className="text-sm text-charcoal/60 mt-1">Tier: {plan.tier}</p>
                        </div>
                        {plan.synced ? (
                          <CheckCircleIcon className="w-6 h-6 text-sage" />
                        ) : (
                          <XCircleIcon className="w-6 h-6 text-tan" />
                        )}
                      </div>
                      {plan.stripe && (
                        <div className="mt-3 pt-3 border-t border-cream">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-charcoal/50">Stripe Product</p>
                              <p className="text-charcoal/80">{plan.stripe.productName || 'Not set'}</p>
                            </div>
                            <div>
                              <p className="text-charcoal/50">Monthly Price</p>
                              <p className="text-charcoal/80">
                                {plan.stripe.monthlyPrice
                                  ? formatCurrency(plan.stripe.monthlyPrice)
                                  : 'Not set'}
                              </p>
                            </div>
                            <div>
                              <p className="text-charcoal/50">Monthly Price ID</p>
                              <p className="text-charcoal/80 text-xs truncate">
                                {plan.stripe.stripePriceIdMonthly || 'Not set'}
                              </p>
                            </div>
                            <div>
                              <p className="text-charcoal/50">Yearly Price ID</p>
                              <p className="text-charcoal/80 text-xs truncate">
                                {plan.stripe.stripePriceIdYearly || 'Not set'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-cream/50 rounded-lg">
                  <h4 className="font-medium text-charcoal mb-2">How to sync a plan:</h4>
                  <ol className="text-sm text-charcoal/70 space-y-1 list-decimal list-inside">
                    <li>Create a Stripe product with the tier name (e.g., "Pro", "Business")</li>
                    <li>Add a <code className="text-rust">tier</code> metadata field matching the tier key (e.g., "pro", "business")</li>
                    <li>Create monthly and/or yearly prices for the product</li>
                    <li>Click "Sync Plans" to update the configuration</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Product Modal */}
      {showCreateProduct && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-warm-white rounded-xl p-6 w-full max-w-md border border-cream shadow-xl">
            <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Create Product</h3>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div>
                <label className="label">Product Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="input"
                  placeholder="e.g., Pro Plan"
                  required
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="input min-h-[80px]"
                  placeholder="Product description..."
                />
              </div>
              <div>
                <label className="label">Tier (for plan sync)</label>
                <select
                  value={newProduct.tier}
                  onChange={(e) => setNewProduct({ ...newProduct, tier: e.target.value })}
                  className="input"
                >
                  <option value="">Select tier...</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
                <p className="text-xs text-charcoal/50 mt-1">
                  Set this to automatically match with a pricing tier
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateProduct(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="btn btn-primary"
                >
                  {isCreating ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Price Modal */}
      {showCreatePrice && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-warm-white rounded-xl p-6 w-full max-w-md border border-cream shadow-xl">
            <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Add Price</h3>
            <form onSubmit={handleCreatePrice} className="space-y-4">
              <div>
                <label className="label">Product *</label>
                <select
                  value={newPrice.productId}
                  onChange={(e) => setNewPrice({ ...newPrice, productId: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select product...</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Price (USD) *</label>
                <input
                  type="number"
                  value={newPrice.unitAmount}
                  onChange={(e) => setNewPrice({ ...newPrice, unitAmount: parseFloat(e.target.value) || 0 })}
                  className="input"
                  placeholder="49.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="label">Billing Interval *</label>
                <select
                  value={newPrice.interval}
                  onChange={(e) => setNewPrice({ ...newPrice, interval: e.target.value as 'month' | 'year' })}
                  className="input"
                >
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div>
                <label className="label">Nickname (optional)</label>
                <input
                  type="text"
                  value={newPrice.nickname}
                  onChange={(e) => setNewPrice({ ...newPrice, nickname: e.target.value })}
                  className="input"
                  placeholder="e.g., Monthly Standard"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreatePrice(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="btn btn-primary"
                >
                  {isCreating ? 'Creating...' : 'Create Price'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
